import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { WhatsAppIcon, PhoneIcon } from "./icons";
import { waLink, telLink, UNIT_PRICE, GIFT_SET, SCENTS, ASSORTED } from "@/config";

export const OrderCalculator = ({ scent, onScentChange }) => {
  const [qty, setQty] = useState(1);
  const [pack, setPack] = useState("single");
  const [giftScents, setGiftScents] = useState(["rose-marry", "lavender-calm", "sandalwood-mist"]);
  const isGift = pack === "gift";

  const toggleGiftScent = (id) =>
    setGiftScents((cur) =>
      cur.includes(id) ? cur.filter((s) => s !== id) : cur.length < 3 ? [...cur, id] : [...cur.slice(1), id]
    );
  const unit = isGift ? GIFT_SET.price : UNIT_PRICE;
  const total = unit * qty;

  return (
    <section id="order-calculator" className="py-20 md:py-28 bg-brandSand/40 border-t border-brandSand">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Reveal>
          <div className="bg-white rounded-3xl border border-brandSand shadow-xl shadow-brandTerracotta/5 p-6 sm:p-10">
            <div className="text-center max-w-lg mx-auto mb-10">
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brandTerracotta">
                Easy Direct Ordering
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold mt-2">Get your candle delivered</h2>
              <p className="text-sm text-brandDarkSlate/70 mt-2">
                Pick a scent, choose single jars or a festive gift set, and connect directly with us.
              </p>
            </div>

            <div className="space-y-7 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-brandDarkSlate/50 mb-3">
                  1 · Your scent
                </p>
                <div className="flex flex-wrap gap-2">
                  {[...SCENTS, ASSORTED].map((s) => {
                    const active = scent?.id === s.id;
                    return (
                      <motion.button
                        key={s.id}
                        data-testid={`order-scent-${s.id}`}
                        onClick={() => onScentChange(s)}
                        whileTap={{ scale: 0.95 }}
                        className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                          active
                            ? "bg-brandTerracotta text-white shadow-md shadow-brandTerracotta/25"
                            : "bg-brandCream border border-brandSand hover:border-brandTerracotta/50"
                        }`}
                      >
                        {s.name}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-brandDarkSlate/50 mb-3">
                  2 · Your pack
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <motion.button
                    data-testid="pack-single-btn"
                    onClick={() => setPack("single")}
                    whileTap={{ scale: 0.98 }}
                    className={`text-left p-5 rounded-2xl border transition-colors ${
                      !isGift
                        ? "border-brandTerracotta ring-1 ring-brandTerracotta/40 bg-brandCream"
                        : "border-brandSand bg-brandCream/50 hover:border-brandTerracotta/40"
                    }`}
                  >
                    <p className="font-bold">Single Jar</p>
                    <p className="text-xs text-brandDarkSlate/70 mt-1">One 5-inch {scent?.name} candle</p>
                    <p className="font-serif font-bold text-xl text-brandTerracotta mt-2">₹{UNIT_PRICE}</p>
                  </motion.button>
                  <motion.button
                    data-testid="pack-gift-btn"
                    onClick={() => setPack("gift")}
                    whileTap={{ scale: 0.98 }}
                    className={`relative text-left p-5 rounded-2xl border transition-colors ${
                      isGift
                        ? "border-brandTerracotta ring-1 ring-brandTerracotta/40 bg-brandCream"
                        : "border-brandSand bg-brandCream/50 hover:border-brandTerracotta/40"
                    }`}
                  >
                    <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.15em] font-bold bg-brandTerracotta text-white px-2.5 py-1 rounded-full">
                      Save ₹{GIFT_SET.savings}
                    </span>
                    <p className="font-bold">Festive Gift Set · {GIFT_SET.jars} Jars</p>
                    <p className="text-xs text-brandDarkSlate/70 mt-1">
                      {scent?.id === "assorted"
                        ? "Diwali-ready gift wrapping, one jar of each scent"
                        : `Diwali-ready gift wrapping, ${GIFT_SET.jars} ${scent?.name} jars — or mix any scents`}
                    </p>
                    <p className="font-serif font-bold text-xl text-brandTerracotta mt-2">
                      ₹{GIFT_SET.price.toLocaleString("en-IN")}
                    </p>
                  </motion.button>
                </div>
              </div>

              {isGift && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-brandDarkSlate/50 mb-3">
                    3 · Pick your 3 scents{" "}
                    <span className="normal-case font-semibold text-brandTerracotta">
                      ({giftScents.length} of 3 chosen)
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SCENTS.map((s) => {
                      const idx = giftScents.indexOf(s.id);
                      const active = idx !== -1;
                      return (
                        <motion.button
                          key={s.id}
                          data-testid={`gift-scent-${s.id}`}
                          onClick={() => toggleGiftScent(s.id)}
                          whileTap={{ scale: 0.95 }}
                          className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                            active
                              ? "bg-brandDarkSlate text-white shadow-md"
                              : "bg-brandCream border border-brandSand hover:border-brandTerracotta/50"
                          }`}
                        >
                          {active && (
                            <span className="w-5 h-5 rounded-full bg-brandTerracotta text-white text-[10px] font-bold flex items-center justify-center">
                              {idx + 1}
                            </span>
                          )}
                          {s.name}
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              <div className="bg-brandCream rounded-2xl p-6 border border-brandSand">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="text-center sm:text-left">
                    <p className="font-bold text-lg" data-testid="order-summary-label">
                      {isGift ? `Festive Gift Set of ${GIFT_SET.jars}` : "5-Inch Perforated Wellness Candle"}
                    </p>
                    <p className="text-xs text-brandDarkSlate/70 mt-0.5">
                      {isGift
                        ? giftScents.map((id) => SCENTS.find((s) => s.id === id)?.name).filter(Boolean).join(" + ")
                        : scent?.name}{" "}
                      · ₹{unit.toLocaleString("en-IN")} per {isGift ? "set" : "jar"}
                    </p>
                  </div>
                  <div className="flex items-center border border-brandDarkSlate/20 rounded-full bg-white p-1">
                    <motion.button
                      data-testid="qty-decrease-btn"
                      whileTap={{ scale: 0.85 }}
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center text-lg font-bold rounded-full hover:bg-brandSand transition-colors"
                      aria-label="Decrease quantity"
                    >
                      −
                    </motion.button>
                    <span data-testid="qty-value" className="w-12 text-center font-bold text-lg tabular-nums">
                      {qty}
                    </span>
                    <motion.button
                      data-testid="qty-increase-btn"
                      whileTap={{ scale: 0.85 }}
                      onClick={() => setQty((q) => q + 1)}
                      className="w-10 h-10 flex items-center justify-center text-lg font-bold rounded-full hover:bg-brandSand transition-colors"
                      aria-label="Increase quantity"
                    >
                      +
                    </motion.button>
                  </div>
                </div>
                <div className="border-t border-brandSand mt-6 pt-4 flex justify-between items-center text-sm">
                  <span className="text-brandDarkSlate/80 font-medium">Estimated Total:</span>
                  <motion.span
                    key={total}
                    data-testid="order-total-price"
                    initial={{ scale: 1.18, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                    className="font-bold text-2xl text-brandTerracotta tabular-nums"
                  >
                    ₹{total.toLocaleString("en-IN")}
                  </motion.span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.a
                data-testid="wa-order-btn"
                href={waLink({
                  scent: scent?.id === "assorted" ? "Assorted (mix of all scents)" : scent?.name,
                  pack,
                  qty,
                  giftScents: giftScents.map((id) => SCENTS.find((s) => s.id === id)?.name).filter(Boolean),
                })}
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-center justify-center gap-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-4 px-6 rounded-2xl shadow-sm hover:shadow-lg transition-shadow"
              >
                <WhatsAppIcon />
                Send WhatsApp Order
              </motion.a>
              <motion.a
                data-testid="phone-order-btn"
                href={telLink}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-center justify-center gap-3 bg-brandDarkSlate hover:bg-black text-white font-semibold py-4 px-6 rounded-2xl shadow-sm hover:shadow-lg transition-shadow"
              >
                <PhoneIcon />
                Call to Confirm
              </motion.a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
